import React, {useState} from 'react';
import {Box, InputBase, Menu, MenuItem} from '@mui/material';
import {Search, Close, KeyboardArrowDown, Check} from '@mui/icons-material';
import {useTranslate} from 'react-admin';
import {t} from '../../themes/statamicTokens';

export const FilterChip = ({label, onOpen, active}) => (
    <Box
        onClick={onOpen}
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            height: 28,
            px: 1.25,
            borderRadius: `${t.radius.sm}px`,
            border: `1px solid ${active ? t.color.accent.primary : t.color.border.default}`,
            backgroundColor: active ? t.color.accent.primaryTint : t.color.surface,
            color: active ? t.color.accent.primary : t.color.text.secondary,
            fontSize: `${t.type.caption.size}px`,
            fontWeight: 500,
            cursor: 'pointer',
            '&:hover': { borderColor: t.color.border.strong },
        }}
    >
        {label}
        <KeyboardArrowDown fontSize="inherit" sx={{fontSize: 14}}/>
    </Box>
);

const BOOLEAN_OPTIONS = [
    {id: true,  label: 'Yes'},
    {id: false, label: 'No'},
];

export const FilterDropdown = ({
    label,
    source,
    value,
    onChange,
    onClear,
    type = 'select',
    options = [],
    getLabel = (o) => o.name ?? o.label ?? String(o.id),
    renderLabel,
}) => {
    const [anchor, setAnchor] = useState(null);
    const open = Boolean(anchor);
    const translate = useTranslate();

    const isBool = type === 'boolean';
    const items = isBool ? BOOLEAN_OPTIONS : options;

    const isActive = value !== undefined && value !== null && value !== '';
    const rawActiveLabel = isActive
        ? (renderLabel
            ? renderLabel(value)
            : (items.find(o => String(o.id) === String(value))?.label
                ?? items.find(o => String(o.id) === String(value))?.name
                ?? String(value)))
        : null;
    // Boolean option labels ("Yes"/"No") are static English — translate for display.
    const activeLabel = isActive && isBool && !renderLabel ? translate(rawActiveLabel) : rawActiveLabel;

    const handleSelect = (id) => {
        onChange?.(source, id);
        setAnchor(null);
    };

    const handleClear = () => {
        onClear?.(source);
        setAnchor(null);
    };

    return (
        <>
            <Box
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.5,
                    height: 28,
                    px: 1.25,
                    borderRadius: `${t.radius.sm}px`,
                    border: `1px solid ${isActive ? t.color.accent.primary : t.color.border.default}`,
                    backgroundColor: isActive ? t.color.accent.primaryTint : t.color.surface,
                    color: isActive ? t.color.accent.primary : t.color.text.secondary,
                    fontSize: `${t.type.caption.size}px`,
                    fontWeight: 500,
                    cursor: 'pointer',
                    '&:hover': { borderColor: t.color.border.strong },
                }}
            >
                {label}
                {isActive && activeLabel && (
                    <Box
                        component="span"
                        sx={{
                            color: t.color.accent.primary,
                            fontWeight: 600,
                        }}
                    >
                        : {activeLabel}
                    </Box>
                )}
                <KeyboardArrowDown fontSize="inherit" sx={{fontSize: 14}}/>
            </Box>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.5,
                            minWidth: 180,
                            borderRadius: `${t.radius.md}px`,
                            border: `1px solid ${t.color.border.default}`,
                            boxShadow: t.shadow.dropdown,
                        },
                    },
                }}
            >
                {isActive && (
                    <MenuItem
                        onClick={handleClear}
                        sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.secondary}}
                    >
                        <Close sx={{fontSize: 14, mr: 1}}/>
                        {translate('Clear')}
                    </MenuItem>
                )}
                {items.map((opt) => {
                    const selected = String(opt.id) === String(value);
                    return (
                        <MenuItem
                            key={String(opt.id)}
                            selected={selected}
                            onClick={() => handleSelect(opt.id)}
                            sx={{fontSize: `${t.type.small.size}px`, color: t.color.text.primary}}
                        >
                            <Box sx={{width: 20, display: 'inline-flex', alignItems: 'center'}}>
                                {selected && <Check sx={{fontSize: 14, color: t.color.accent.primary}}/>}
                            </Box>
                            {isBool ? translate(opt.label) : getLabel(opt)}
                        </MenuItem>
                    );
                })}
            </Menu>
        </>
    );
};

export const ActiveFilterChip = ({label, onRemove}) => (
    <Box
        sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            height: 28,
            pl: 1.25,
            pr: 0.5,
            borderRadius: `${t.radius.sm}px`,
            border: `1px solid ${t.color.accent.primary}`,
            backgroundColor: t.color.status.infoTint,
            color: t.color.accent.primary,
            fontSize: `${t.type.caption.size}px`,
            fontWeight: 500,
        }}
    >
        {label}
        <Box
            onClick={onRemove}
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 18,
                height: 18,
                borderRadius: '50%',
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'rgba(59,130,246,0.15)' },
            }}
        >
            <Close sx={{fontSize: 12}}/>
        </Box>
    </Box>
);

const FilterBar = ({
    searchValue,
    onSearchChange,
    placeholder = 'Search',
    filters,
    rightSlot,
}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 3,
            py: 1.5,
            borderBottom: `1px solid ${t.color.border.hairline}`,
            flexWrap: 'wrap',
        }}
    >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                flex: '1 1 320px',
                minWidth: 240,
                height: 32,
                px: 1.25,
                borderRadius: `${t.radius.sm}px`,
                border: `1px solid ${t.color.border.default}`,
                backgroundColor: t.color.surface,
                '&:focus-within': {
                    borderColor: t.color.accent.primary,
                    boxShadow: `0 0 0 3px ${t.color.status.infoTint}`,
                },
            }}
        >
            <Search sx={{fontSize: 16, color: t.color.text.muted, mr: 1}}/>
            <InputBase
                value={searchValue ?? ''}
                onChange={(e) => onSearchChange?.(e.target.value)}
                placeholder={placeholder}
                sx={{
                    flex: 1,
                    fontSize: `${t.type.small.size}px`,
                    color: t.color.text.primary,
                    '& input::placeholder': { color: t.color.text.muted, opacity: 1 },
                }}
            />
        </Box>

        {filters}

        {rightSlot && <Box sx={{ml: 'auto', display: 'flex', gap: 1}}>{rightSlot}</Box>}
    </Box>
);

export default FilterBar;
